/**
 * Trainer view of a single case — info + sessions list + new session.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { Mast } from "../components/Mast.js";
import { api } from "../lib/api.js";

interface Case {
  id: string; trainer_id: string; case_user_id: string | null;
  name: string; birth_date: string | null; notes_enc: string | null;
  created_at: string;
}
interface SessionRow {
  id: string; scheduled_for: string; estimated_min: number; state: string;
  started_at: string | null; ended_at: string | null; protocol_hint: string | null;
}

export default function CaseDetail() {
  const { id } = useParams<{ id: string }>();
  const [cs, setCs] = useState<Case | null>(null);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [recentToken, setRecentToken] = useState<{ url: string; expires: string } | null>(null);

  async function load() {
    setLoading(true);
    const [c, s] = await Promise.all([
      api<{ ok: true; case: Case }>(`/api/cases/${id}`),
      api<{ ok: true; sessions: SessionRow[] }>(`/api/cases/${id}/sessions`),
    ]);
    setCs(c.case);
    setSessions(s.sessions);
    setLoading(false);
  }
  useEffect(() => { if (id) load(); }, [id]);

  if (loading || !cs) return <><Mast section="個案詳細" /><main className="page muted">載入中…</main></>;

  return (
    <>
      <Mast section={`個案 · ${cs.name}`} />
      <main className="page">
        <Link to="/dashboard" style={{ fontSize: "var(--t-sm)", color: "var(--ink-soft)", display: "inline-block", marginBottom: "var(--s-4)" }}>
          ← 回個案列表
        </Link>

        <section style={{ marginBottom: "var(--s-7)" }}>
          <div className="serial rise rise-1" style={{ marginBottom: "var(--s-3)" }}>CASE · {cs.id.slice(0, 12)}</div>
          <h1 className="rise rise-2"><span className="italic-display">{cs.name}</span></h1>
          <div className="row rise rise-3" style={{ marginTop: "var(--s-3)", gap: "var(--s-5)", fontSize: "var(--t-sm)" }}>
            <span className="muted">出生 <span className="tabular">{cs.birth_date ?? "—"}</span></span>
            <span className="muted">建立 <span className="tabular">{cs.created_at.slice(0, 10)}</span></span>
            <span>{cs.case_user_id
              ? <span className="chip chip--olive">activated</span>
              : <span className="chip chip--petrol">intake (尚未掃 join)</span>}</span>
          </div>
          {cs.notes_enc && (
            <div className="alert alert--olive rise rise-4" style={{ marginTop: "var(--s-4)", maxWidth: 720 }}>
              <strong>備註：</strong> {cs.notes_enc}
            </div>
          )}
          <div className="row rise rise-5" style={{ marginTop: "var(--s-5)", gap: "var(--s-3)" }}>
            <button className="btn btn--rust" onClick={() => setShowNew(true)}>＋ 排定 session</button>
          </div>
        </section>

        {/* Recently issued join token banner */}
        {recentToken && (
          <div className="surface rise" style={{ padding: "var(--s-5)", borderColor: "var(--rust)", marginBottom: "var(--s-6)" }}>
            <div className="serial" style={{ color: "var(--rust)", marginBottom: "var(--s-2)" }}>JOIN URL · 寄給個案</div>
            <div style={{ fontFamily: "var(--mono)", fontSize: "var(--t-sm)", padding: "var(--s-3)", background: "var(--paper-deep)", borderRadius: "var(--r-sm)", wordBreak: "break-all" }}>
              {recentToken.url}
            </div>
            <div className="row" style={{ marginTop: "var(--s-3)", gap: "var(--s-3)" }}>
              <button className="btn btn--small" onClick={() => navigator.clipboard.writeText(recentToken.url)}>
                複製連結
              </button>
              <span className="muted" style={{ fontSize: "var(--t-xs)" }}>到期：{recentToken.expires}</span>
            </div>
          </div>
        )}

        <section>
          <div className="section-head">
            <div>
              <div className="eyebrow">Sessions · {sessions.length} 次</div>
              <h2>歷次訓練</h2>
            </div>
          </div>

          {sessions.length === 0 ? (
            <div className="empty">
              <div className="muted">尚未排定任何 session</div>
            </div>
          ) : (
            <div className="list">
              <div className="list-row" style={{
                borderBottom: "1px solid var(--ink)",
                paddingBottom: "var(--s-2)",
                marginBottom: "var(--s-1)",
              }}>
                <div className="serial">日期</div>
                <div className="serial">時長</div>
                <div className="serial">狀態</div>
                <div className="serial" style={{ textAlign: "right" }}>動作</div>
              </div>
              {sessions.map(s => (
                <div key={s.id} className="list-row">
                  <div>
                    <div className="list-row__name tabular">{fmt(s.scheduled_for)}</div>
                    <div className="list-row__meta">{s.id.slice(0, 12)}</div>
                  </div>
                  <div className="muted tabular">{s.estimated_min} 分</div>
                  <div><StateChip state={s.state} /></div>
                  <div style={{ textAlign: "right" }}>
                    <Link to={`/dashboard/sessions/${s.id}`} style={{ fontSize: "var(--t-sm)" }}>詳細 →</Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {showNew && (
        <NewSessionModal
          caseId={cs.id}
          onClose={() => setShowNew(false)}
          onCreated={(joinUrl, expires) => {
            setRecentToken({ url: joinUrl, expires });
            setShowNew(false);
            load();
          }}
        />
      )}
    </>
  );
}

function StateChip({ state }: { state: string }) {
  if (state === "live")    return <span className="chip chip--olive">live</span>;
  if (state === "ended")   return <span className="chip">ended</span>;
  if (state === "aborted") return <span className="chip chip--danger">aborted</span>;
  if (state === "lobby")   return <span className="chip chip--petrol">lobby</span>;
  return <span className="chip chip--rust">{state}</span>;
}

function fmt(s: string) {
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("zh-TW", { month: "short", day: "numeric", weekday: "short", hour: "2-digit", minute: "2-digit" });
}

function NewSessionModal({ caseId, onClose, onCreated }: {
  caseId: string;
  onClose: () => void;
  onCreated: (joinUrl: string, expires: string) => void;
}) {
  const [date, setDate] = useState(() => {
    const d = new Date(); d.setMinutes(d.getMinutes() + 30); return d.toISOString().slice(0, 16);
  });
  const [estimatedMin, setEstimatedMin] = useState(30);
  const [source, setSource] = useState("eeg");
  const [game, setGame]     = useState("plane");
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const r = await api<{ ok: true; session: { join_url: string; join_expires_at: string } }>("/api/sessions", {
        method: "POST",
        body: {
          case_id: caseId,
          scheduled_for: new Date(date).toISOString(),
          estimated_min: estimatedMin,
          protocol_hint: { source, game },
        },
      });
      onCreated(r.session.join_url, r.session.join_expires_at);
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? "Failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onSubmit={submit} onClick={(e) => e.stopPropagation()}>
        <div className="serial" style={{ marginBottom: "var(--s-2)" }}>New session</div>
        <h3 className="modal__title">排定新 session</h3>
        <div className="modal__sub">送出後會生成個案專屬 join URL（24 小時有效）。</div>

        <div className="col" style={{ gap: "var(--s-4)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "var(--s-3)" }}>
            <div className="field">
              <label className="field-label">時間</label>
              <input type="datetime-local" required value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label">時長 (分)</label>
              <input type="number" min={5} max={180} value={estimatedMin}
                     onChange={e => setEstimatedMin(parseInt(e.target.value, 10) || 30)} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--s-3)" }}>
            <div className="field">
              <label className="field-label">訊號來源 (建議)</label>
              <select value={source} onChange={e => setSource(e.target.value)}>
                <option value="eeg">EEG (sgimacog)</option>
                <option value="hrv">HRV (VisioMynd)</option>
                <option value="eeg+hrv">EEG + HRV</option>
                <option value="none">不接訊號</option>
              </select>
            </div>
            <div className="field">
              <label className="field-label">遊戲 (建議)</label>
              <select value={game} onChange={e => setGame(e.target.value)}>
                <option value="plane">SoraMynd plane</option>
                <option value="baseball">SoraMynd baseball</option>
                <option value="zentangle">SoraMynd zentangle</option>
                <option value="mynd-nback">MyndScape n-back</option>
              </select>
            </div>
          </div>
          {error && <div className="alert">{error}</div>}
        </div>

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>取消</button>
          <button type="submit" className="btn btn--rust" disabled={busy}>
            {busy ? "建立中…" : "建立並產生 join URL"}
          </button>
        </div>
      </form>
    </div>
  );
}
